<template>
  <transition name="winner-modal-fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-hidden select-none"
      @click.self="close"
    >
      <!-- Dynamic Particle Canvas in Front of Card (Gold, Red & Emerald Confetti/Stars/Coins) -->
      <canvas
        ref="confettiCanvas"
        class="absolute inset-0 pointer-events-none z-30"
      ></canvas>

      <!-- Main Winner Dialog Card (Responsive Flex & Scrollable for Small Screens) -->
      <div
        class="relative z-10 w-full max-w-sm max-h-[90vh] flex flex-col rounded-3xl bg-white border-2 border-amber-400 p-0 shadow-2xl shadow-red-500/40 text-center overflow-hidden transform transition-all duration-500 scale-100 animate-winner-pop"
      >
        <!-- Background Ambient Glow & Lighting -->
        <div
          class="absolute -top-20 -left-20 w-44 h-44 bg-amber-500/20 rounded-full blur-3xl pointer-events-none"
        ></div>
        <div
          class="absolute -bottom-20 -right-20 w-44 h-44 bg-red-600/20 rounded-full blur-3xl pointer-events-none"
        ></div>

        <!-- Header Banner: Red Gradient with Gold Accents (Fixed Top Header) -->
        <div
          class="relative flex-shrink-0 bg-gradient-to-r from-red-600 via-rose-600 to-red-700 pt-6 pb-6 px-5 text-white overflow-hidden shadow-inner"
        >
          <!-- Background Pattern Glows -->
          <div
            class="absolute -top-12 -left-12 w-32 h-32 bg-amber-400/25 rounded-full blur-2xl pointer-events-none"
          ></div>
          <div
            class="absolute -bottom-12 -right-12 w-32 h-32 bg-yellow-300/25 rounded-full blur-2xl pointer-events-none"
          ></div>

          <!-- Top Close Button -->
          <button
            @click="close"
            class="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 active:scale-95 text-white flex items-center justify-center transition-all z-20 shadow-md"
          >
            <fa icon="fa-solid fa-times" class="text-base" />
          </button>

          <!-- SuperWin Brand Logo -->
          <div class="flex justify-center mb-2">
            <img
              src="logo.svg"
              alt="SuperWin Logo"
              class="h-10 w-auto object-contain drop-shadow-lg"
            />
          </div>

          <!-- Golden Trophy Medallion -->
          <div
            class="relative mx-auto mb-2 w-16 h-16 flex items-center justify-center"
          >
            <!-- Pulsing Ring -->
            <div
              class="absolute inset-0 rounded-full bg-amber-300/50 blur-md animate-ping"
            ></div>
            <div
              class="relative w-14 h-14 rounded-full bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-200 p-1 shadow-lg shadow-amber-500/50 flex items-center justify-center"
            >
              <div
                class="w-full h-full rounded-full bg-red-700 flex items-center justify-center border-2 border-amber-300"
              >
                <fa
                  icon="fa-solid fa-trophy"
                  class="text-2xl text-amber-300 drop-shadow-[0_2px_10px_rgba(251,191,36,0.9)] animate-bounce"
                />
              </div>
            </div>
            <!-- Gold Star Badge -->
            <div class="absolute -top-1 -right-1 text-yellow-300 animate-pulse">
              <fa icon="fa-solid fa-star" class="text-base drop-shadow-md" />
            </div>
          </div>

          <!-- Congratulations Title -->
          <h2
            class="text-lg font-black text-amber-200 tracking-wide uppercase drop-shadow-sm"
          >
            {{ t(["Congratulations!", "ຂໍສະແດງຄວາມຍິນດີ!"]) }}
            {{ t(["You won the prize!", "ທ່ານຖືກລາງວັນ!"]) }}
          </h2>

          <!-- Win Remark Gold Pill -->
          <div
            v-if="prizeData && prizeData.winRemark"
            class="inline-block mt-2 px-3 py-1 rounded-full bg-amber-400/20 border border-amber-300/50 text-amber-100 font-extrabold text-[11px] tracking-wider shadow-inner"
          >
            ✨ {{ prizeData.winRemark }}
          </div>
        </div>

        <!-- Body Section: Scrollable Content Area for Small Screen Safety -->
        <div class="p-5 bg-white relative flex-1 overflow-y-auto">
          <!-- Big Winning Amount Card (Wealth & Prosperity: Emerald Green & Gold Gradient) -->
          <div
            class="my-2 py-4 px-4 rounded-2xl bg-gradient-to-br from-emerald-50 via-teal-50/70 to-amber-50 border-2 border-emerald-400/70 shadow-md flex flex-col items-center justify-center relative overflow-hidden group"
          >
            <div
              class="absolute top-0 right-0 -mt-2 -mr-2 w-14 h-14 bg-emerald-400/15 rounded-full blur-sm"
            ></div>

            <div
              class="flex items-center gap-1.5 text-xs font-extrabold text-emerald-800 uppercase tracking-widest mb-1"
            >
              <fa icon="fa-solid fa-coins" class="text-amber-500 text-sm" />
              <span>{{ t(["Winning Amount", "ຈຳນວນເງິນລາງວັນ"]) }}</span>
            </div>

            <!-- Amount Text: Gold & Emerald Glow -->
            <div
              class="text-3xl font-black tracking-tight text-emerald-700 drop-shadow-sm my-1"
            >
              {{ displayAmount | formatnumber }}
              <span class="text-lg font-bold text-emerald-800">ກີບ</span>
            </div>

            <!-- Bonus Pro Amount Breakdown Badge -->
            <div
              v-if="prizeData && prizeData.winProAmount > 0"
              class="mt-1 px-3 py-0.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center gap-1 shadow-sm"
            >
              <fa icon="fa-solid fa-gift" class="text-yellow-300" />
              +{{ prizeData.winProAmount | formatnumber }} ກີບ
              {{ t(["Bonus", "ເງິນແຖມລາງວັນ"]) }}
            </div>
          </div>

          <!-- Bill & Round Details -->
          <div
            v-if="
              prizeData &&
              (prizeData.billNumber || prizeData.roundId || prizeData.billCount)
            "
            class="grid grid-cols-2 gap-2 text-left my-4"
          >
            <div
              v-if="prizeData.billNumber"
              class="bg-gray-50 p-2.5 rounded-xl border border-gray-200 flex flex-col"
            >
              <span class="text-[10px] text-gray-500 uppercase font-bold">{{
                t(["Bill No.", "ເລກໃບບິນ"])
              }}</span>
              <span
                class="text-xs font-mono font-extrabold text-red-600 truncate mt-0.5"
                >{{ prizeData.billNumber }}</span
              >
            </div>
            <div
              v-if="prizeData.roundId"
              class="bg-gray-50 p-2.5 rounded-xl border border-gray-200 flex flex-col"
            >
              <span class="text-[10px] text-gray-500 uppercase font-bold">{{
                t(["Round ID", "ງວດທີ"])
              }}</span>
              <span
                class="text-xs font-mono font-extrabold text-red-600 truncate mt-0.5"
                >#{{ prizeData.roundId }}</span
              >
            </div>
          </div>

          <!-- Action Buttons (Buy Lottery + View Bill) -->
          <div class="flex flex-col gap-2 mt-4">
            <!-- Primary CTA Button: Buy Lottery (ຊື້ຫວຍ) -->
            <button
              @click="onBuyLottery"
              class="w-full py-3.5 px-6 rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-red-700 hover:from-red-500 hover:to-rose-600 active:scale-[0.98] text-white font-extrabold text-base shadow-lg shadow-red-500/35 transition-all flex items-center justify-center gap-2 cursor-pointer border-2 border-amber-300"
            >
              <fa
                icon="fa-solid fa-shopping-cart"
                class="text-lg text-yellow-300"
              />
              <span>{{ t(["Buy Lottery", "ຊື້ຫວຍ"]) }}</span>
            </button>

            <!-- Secondary Button: View Bill (ເບິ່ງໃບບິນ) -->
            <button
              @click="onViewBill"
              class="w-full py-3 px-6 rounded-2xl bg-amber-50 hover:bg-amber-100 active:scale-[0.98] text-amber-900 font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer border-2 border-amber-300 shadow-sm"
            >
              <fa
                icon="fa-solid fa-file-invoice"
                class="text-base text-amber-700"
              />
              <span>{{ t(["View Bill", "ເບິ່ງໃບບິນ"]) }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "WinnerPrizeModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    prizeData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      displayAmount: 0,
      animTimer: null,
      particleAnimId: null,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.startAmountAnimation();
        this.$nextTick(() => {
          this.initConfetti();
        });
      } else {
        this.stopConfetti();
      }
    },
  },
  beforeDestroy() {
    this.stopConfetti();
    if (this.animTimer) clearInterval(this.animTimer);
  },
  methods: {
    close() {
      this.$emit("close");
      this.$emit("update:visible", false);
    },
    onBuyLottery() {
      this.close();
      if (this.$router && this.$route.path !== "/buy") {
        this.$router.push("/buy").catch(() => {});
      }
    },
    onViewBill() {
      this.close();
      if (this.$router && this.$route.path !== "/buyhistory") {
        this.$router.push("/buyhistory").catch(() => {});
      }
    },
    onClaim() {
      this.onViewBill();
    },
    startAmountAnimation() {
      const target = Number(
        this.prizeData.totalWin || this.prizeData.winAmount || 0,
      );
      this.displayAmount = 0;
      if (target <= 0) return;

      const duration = 1200; // ms
      const steps = 30;
      const stepTime = duration / steps;
      const increment = target / steps;
      let current = 0;

      if (this.animTimer) clearInterval(this.animTimer);
      this.animTimer = setInterval(() => {
        current += increment;
        if (current >= target) {
          this.displayAmount = target;
          clearInterval(this.animTimer);
        } else {
          this.displayAmount = Math.floor(current);
        }
      }, stepTime);
    },
    initConfetti() {
      const canvas = this.$refs.confettiCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const width = (canvas.width = window.innerWidth);
      const height = (canvas.height = window.innerHeight);

      // Gold, Emerald Green, Red & White Shiny Palette
      const colors = [
        "#f59e0b",
        "#fbbf24",
        "#fef08a",
        "#d97706",
        "#ef4444",
        "#dc2626",
        "#10b981",
        "#059669",
        "#ffffff",
      ];
      const particles = [];
      const particleCount = 90;

      for (let i = 0; i < particleCount; i++) {
        const randShape = Math.random();
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height - height,
          size: Math.random() * 8 + 4,
          color: colors[Math.floor(Math.random() * colors.length)],
          vx: (Math.random() - 0.5) * 3,
          vy: Math.random() * 4 + 2,
          rotation: Math.random() * 360,
          vRot: (Math.random() - 0.5) * 6,
          shape: randShape > 0.6 ? "rect" : randShape > 0.2 ? "circle" : "star",
        });
      }

      const render = () => {
        ctx.clearRect(0, 0, width, height);

        particles.forEach((p) => {
          p.x += p.vx;
          p.y += p.vy;
          p.rotation += p.vRot;

          if (p.y > height) {
            p.y = -20;
            p.x = Math.random() * width;
          }

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = p.color;

          if (p.shape === "rect") {
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 1.6);
          } else if (p.shape === "circle") {
            ctx.beginPath();
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
            ctx.fill();
          } else {
            // Draw 5-pointed star
            ctx.beginPath();
            for (let i = 0; i < 5; i++) {
              ctx.lineTo(
                Math.cos(((18 + i * 72) * Math.PI) / 180) * p.size,
                -Math.sin(((18 + i * 72) * Math.PI) / 180) * p.size,
              );
              ctx.lineTo(
                Math.cos(((54 + i * 72) * Math.PI) / 180) * (p.size / 2),
                -Math.sin(((54 + i * 72) * Math.PI) / 180) * (p.size / 2),
              );
            }
            ctx.closePath();
            ctx.fill();
          }

          ctx.restore();
        });

        this.particleAnimId = requestAnimationFrame(render);
      };

      render();
    },
    stopConfetti() {
      if (this.particleAnimId) {
        cancelAnimationFrame(this.particleAnimId);
        this.particleAnimId = null;
      }
    },
  },
};
</script>

<style scoped>
@keyframes winnerPop {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-winner-pop {
  animation: winnerPop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.winner-modal-fade-enter-active,
.winner-modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.winner-modal-fade-enter,
.winner-modal-fade-leave-to {
  opacity: 0;
}
</style>
